import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { toast } from "@/components/ui/use-toast";
import { useAuth } from '@/contexts/AuthContext';
import { fixFirstPostBadge } from '@/lib/fixFirstPostBadge';
import { ensureAllBadgesExist } from '@/lib/supabase';
import { supabase } from '@/integrations/supabase/client';
import MainLayout from '@/components/MainLayout';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle } from 'lucide-react';
import { repairAllUserBadges } from '@/lib/repairAllUserBadges';
import { fixFirstPostBadgeSafe } from '@/lib/adminFixBadges';
import { fixFirstCommentBadgeSafe } from '@/lib/adminFixBadges';

const AdminTools = () => {
  const { user } = useAuth();
  const [isFixingBadges, setIsFixingBadges] = useState(false);
  const [isFixingFirstPostBadge, setIsFixingFirstPostBadge] = useState(false);
  const [isRepairingAllBadges, setIsRepairingAllBadges] = useState(false);
  const [results, setResults] = useState<string[]>([]);
  
  const addResult = (message: string) => {
    setResults(prev => [...prev, message]);
  };
  
  const handleFixBadges = async () => {
    setIsFixingBadges(true);
    addResult("Starting badge system repair...");
    
    try {
      const success = await ensureAllBadgesExist();
      
      if (success) {
        addResult("✅ All badges successfully ensured in the database");
        toast({
          title: "Badge System Repaired",
          description: "All badge definitions have been successfully ensured",
          variant: "default",
        });
      } else {
        addResult("❌ Failed to ensure all badges exist");
        toast({
          title: "Badge Repair Failed",
          description: "Could not repair all badge definitions",
          variant: "destructive",
        });
      }
    } catch (error) {
      addResult(`❌ Error fixing badges: ${error instanceof Error ? error.message : 'Unknown error'}`);
      console.error('Error fixing badges:', error);
      toast({
        title: "Badge Repair Error",
        description: "An unexpected error occurred while fixing badges",
        variant: "destructive",
      });
    } finally {
      setIsFixingBadges(false);
    }
  };
  
  const handleFixFirstPostBadge = async () => {
    setIsFixingFirstPostBadge(true);
    addResult("Starting First Post badge repair for all users...");
    
    try {
      // Find all users who have made posts
      const { data: posts, error: postsError } = await supabase
        .from('posts')
        .select('user_id')
        .order('created_at', { ascending: true });
      
      if (postsError) {
        throw new Error(postsError.message || 'Error fetching posts');
      }
      
      // Get unique user IDs
      const uniqueUserIds = [...new Set(posts.map(post => post.user_id))];
      addResult(`Found ${uniqueUserIds.length} users who have created posts`);
      
      // Fix the First Post badge for each user
      let successCount = 0;
      let errorCount = 0;
      
      for (const userId of uniqueUserIds) {
        try {
          const success = await fixFirstPostBadge(userId);
          if (success) {
            successCount++;
          } else {
            errorCount++;
          }
        } catch (error) {
          console.error(`Error fixing First Post badge for user ${userId}:`, error);
          errorCount++;
        }
      }
      
      addResult(`✅ Successfully fixed First Post badge for ${successCount} users`);
      if (errorCount > 0) {
        addResult(`❌ Failed to fix First Post badge for ${errorCount} users`);
      }
      
      toast({
        title: "First Post Badge Repair Complete",
        description: `Fixed for ${successCount} users, ${errorCount} failures`,
        variant: errorCount > 0 ? "default" : "destructive",
      });
    } catch (error) {
      addResult(`❌ Error fixing First Post badge: ${error instanceof Error ? error.message : 'Unknown error'}`);
      console.error('Error fixing First Post badge:', error);
      toast({
        title: "First Post Badge Repair Error",
        description: "An unexpected error occurred while fixing the First Post badge",
        variant: "destructive",
      });
    } finally {
      setIsFixingFirstPostBadge(false);
    }
  };
  
  const handleRepairAllBadges = async () => {
    setIsRepairingAllBadges(true);
    addResult("Starting comprehensive badge repair for all users...");
    
    try {
      const success = await repairAllUserBadges();
      
      if (success) {
        addResult("✅ All user badges successfully repaired");
        toast({
          title: "All Badges Repaired",
          description: "All user badges have been checked and repaired based on user activity",
          variant: "default",
        });
      } else {
        addResult("❌ Failed to repair all user badges");
        toast({
          title: "Badge Repair Failed",
          description: "Could not repair all user badges",
          variant: "destructive",
        });
      }
    } catch (error) {
      addResult(`❌ Error repairing all badges: ${error instanceof Error ? error.message : 'Unknown error'}`);
      console.error('Error repairing all badges:', error);
      toast({
        title: "Badge Repair Error",
        description: "An unexpected error occurred while repairing all badges",
        variant: "destructive",
      });
    } finally {
      setIsRepairingAllBadges(false);
    }
  };
  
  if (!user) {
    return <MainLayout>
      <Card>
        <CardHeader>
          <CardTitle>Admin Tools</CardTitle>
          <CardDescription>You must be logged in to access admin tools</CardDescription>
        </CardHeader>
      </Card>
    </MainLayout>;
  }
  
  return (
    <MainLayout>
      <div className="container mx-auto p-4">
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Admin Tools</CardTitle>
            <CardDescription>Tools to fix common database issues</CardDescription>
          </CardHeader>
          <CardContent>
            <Alert className="mb-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Warning</AlertTitle>
              <AlertDescription>
                These tools make direct changes to the database. Use with caution.
              </AlertDescription>
            </Alert>
            
            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>Badge System</CardTitle>
                  <CardDescription>Fix badge definitions in the database</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button 
                    onClick={handleFixBadges} 
                    disabled={isFixingBadges}
                    className="w-full"
                  >
                    {isFixingBadges ? "Fixing Badges..." : "Fix All Badges"}
                  </Button>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>First Post Badge</CardTitle>
                  <CardDescription>Fix the First Post badge for all users</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button 
                    onClick={handleFixFirstPostBadge} 
                    disabled={isFixingFirstPostBadge}
                    className="w-full"
                  >
                    {isFixingFirstPostBadge ? "Fixing Badge..." : "Fix First Post Badge"}
                  </Button>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Repair All User Badges</CardTitle>
                  <CardDescription>Check and award badges to users based on their activity</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col gap-3">
                    <Button 
                      onClick={handleRepairAllBadges}
                      disabled={isRepairingAllBadges}
                      className="w-full"
                    >
                      {isRepairingAllBadges ? "Repairing All Badges..." : "Repair All User Badges"}
                    </Button>
                    
                    <Button 
                      onClick={async () => {
                        toast({
                          title: "Repairing Badges",
                          description: "Starting badge repair process. This may take a moment...",
                        });
                        
                        addResult("Starting First Post badge repair for all users...");
                        
                        try {
                          // First, get all users
                          const { data: users, error: usersError } = await supabase
                            .from("profiles")
                            .select("id");
                            
                          if (usersError) {
                            throw new Error(`Error fetching users: ${usersError.message}`);
                          }
                          
                          if (!users || users.length === 0) {
                            toast({
                              title: "No Users Found",
                              description: "No users were found to repair badges for.",
                              variant: "destructive",
                            });
                            addResult("❌ No users found in the database");
                            return;
                          }
                          
                          addResult(`Found ${users.length} users in the database`);
                          
                          // Fix First Post badge for all users
                          let successCount = 0;
                          let errorCount = 0;
                          
                          // Process in batches to avoid overwhelming the UI
                          const batchSize = 10;
                          for (let i = 0; i < users.length; i += batchSize) {
                            const batch = users.slice(i, i + batchSize);
                            
                            // Process each user in the batch using our safe function
                            const results = await Promise.allSettled(batch.map(user => fixFirstPostBadgeSafe(user.id)));
                            
                            results.forEach(result => {
                              if (result.status === 'fulfilled' && result.value) {
                                successCount++;
                              } else {
                                errorCount++;
                              }
                            });
                            
                            // Update progress every few batches
                            if (i % 20 === 0 && i > 0) {
                              addResult(`Processed ${i}/${users.length} users...`);
                            }
                          }
                          
                          addResult(`✅ Successfully fixed First Post badge for ${successCount} users`);
                          if (errorCount > 0) {
                            addResult(`❌ Failed to fix First Post badge for ${errorCount} users`);
                          }
                          
                          toast({
                            title: "Badges Repaired",
                            description: `First Post badge has been checked for ${users.length} users. Fixed ${successCount} badges.`,
                          });
                        } catch (error) {
                          const errorMessage = error instanceof Error ? error.message : "Unknown error";
                          addResult(`❌ Error repairing badges: ${errorMessage}`);
                          
                          toast({
                            title: "Badge Repair Failed",
                            description: "There was an error repairing badges. Check the console for details.",
                            variant: "destructive",
                          });
                        }
                      }}
                      className="w-full"
                    >
                      Repair First Post Badges (Safe)
                    </Button>
                    
                    <Button 
                      onClick={async () => {
                        toast({
                          title: "Repairing Comment Badges",
                          description: "Starting comment badge repair process. This may take a moment...",
                        });
                        
                        addResult("Starting First Comment badge repair for all users...");
                        
                        try {
                          // First, get all users
                          const { data: users, error: usersError } = await supabase
                            .from("profiles")
                            .select("id");
                            
                          if (usersError) {
                            throw new Error(`Error fetching users: ${usersError.message}`);
                          }
                          
                          if (!users || users.length === 0) {
                            toast({
                              title: "No Users Found",
                              description: "No users were found to repair badges for.",
                              variant: "destructive",
                            });
                            addResult("❌ No users found in the database");
                            return;
                          }
                          
                          addResult(`Found ${users.length} users in the database`);
                          
                          // Fix First Comment badge for all users
                          let successCount = 0;
                          let errorCount = 0;
                          
                          // Process in batches to avoid overwhelming the UI
                          const batchSize = 10;
                          for (let i = 0; i < users.length; i += batchSize) {
                            const batch = users.slice(i, i + batchSize);
                            
                            // Process each user in the batch using our safe function
                            const results = await Promise.allSettled(batch.map(user => fixFirstCommentBadgeSafe(user.id)));
                            
                            results.forEach(result => {
                              if (result.status === 'fulfilled' && result.value) {
                                successCount++;
                              } else {
                                errorCount++;
                              }
                            });
                            
                            // Update progress every few batches
                            if (i % 20 === 0 && i > 0) {
                              addResult(`Processed ${i}/${users.length} users...`);
                            }
                          }
                          
                          addResult(`✅ Successfully fixed First Comment badge for ${successCount} users`);
                          if (errorCount > 0) {
                            addResult(`❌ Failed to fix First Comment badge for ${errorCount} users`);
                          }
                          
                          toast({
                            title: "Comment Badges Repaired",
                            description: `First Comment badge has been checked for ${users.length} users. Fixed ${successCount} badges.`,
                          });
                        } catch (error) {
                          const errorMessage = error instanceof Error ? error.message : "Unknown error";
                          addResult(`❌ Error repairing comment badges: ${errorMessage}`);
                          
                          toast({
                            title: "Comment Badge Repair Failed",
                            description: "There was an error repairing comment badges. Check the console for details.",
                            variant: "destructive",
                          });
                        }
                      }}
                      className="w-full"
                    >
                      Repair First Comment Badges (Safe)
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </CardContent>
        </Card>
        
        {results.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Operation Results</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-muted p-4 rounded-md text-sm font-mono h-96 overflow-y-auto">
                {results.map((result, index) => (
                  <div key={index} className="mb-1">{result}</div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </MainLayout>
  );
};

export default AdminTools; 